import { useCallback, useMemo } from 'react';
import { Button, Tooltip } from '@robocorp/components';
import { useTranslation } from 'react-i18next';
import { useSelector } from '~/hooks';
import { useAddWorkspaceMemberMutation, useWorkspaceMembersLazyQuery } from '~/queries';

interface Props {
  organizationId: string;
  workspaceId: string;
}

export default function JoinWorkspaceButton({ organizationId, workspaceId }: Props) {
  const { t } = useTranslation();

  const [{ data: workspaceMembers = [], isLoading: isLoadingMembers }] =
    useWorkspaceMembersLazyQuery(workspaceId);
  const joinWorkspaceMutation = useAddWorkspaceMemberMutation();

  const currentUser = useSelector((state) => state.userResources.users.current);

  const userIsNotAMember = useMemo(
    () => !workspaceMembers.filter(({ id }) => id === currentUser?.id).length,
    [workspaceMembers, currentUser],
  );

  const handleJoinWorkspace = useCallback(() => {
    joinWorkspaceMutation.mutate({
      userId: currentUser.id,
      organizationId,
      workspaceId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingMembers || !userIsNotAMember) return null;

  return (
    <Tooltip text={t('joinThisWorkspace')} placement="top">
      <Button
        size="small"
        variant="secondary"
        onClick={handleJoinWorkspace}
        loading={joinWorkspaceMutation.isLoading}
      >
        {t('join')}
      </Button>
    </Tooltip>
  );
}
